import React, { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from './MyModal'
import MyInput from './MyInput'
import MyButton from './buttons'
import { IoMdMail } from 'react-icons/io'

interface SendMailModalProps {
  isOpen: boolean
  onClose: () => void
  recipientEmail: string
  recipientName: string
}

const SendMailModal: React.FC<SendMailModalProps> = ({
  isOpen,
  onClose,
  recipientEmail,
  recipientName,
}) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
  })
  const [captchaValue, setCaptchaValue] = useState('')
  const [captchaError, setCaptchaError] = useState('')
  const honeyPotRef = useRef<HTMLInputElement>(null)
  const [formStartTime] = useState(Date.now())

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10)
    const num2 = Math.floor(Math.random() * 10)
    return { num1, num2, result: num1 + num2 }
  }

  const [captcha] = useState(generateCaptcha())

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = () => {
    // Vérification du honeypot
    if (honeyPotRef.current?.value) {
      console.log('Spam détecté via honeypot')
      return
    }

    // Vérification du temps minimum (3 secondes) - Antispam ma gueule
    const timeSpent = Date.now() - formStartTime
    if (timeSpent < 3000) {
      console.log('Remplissage trop rapide détecté')
      return
    }

    // Vérification du captcha
    if (Number(captchaValue) !== captcha.result) {
      setCaptchaError(t('invalid-captcha'))
      return
    }

    // Vérification de mots-clés spam courants
    const spamKeywords = ['viagra', 'casino', 'lottery', 'winner', 'debt']
    const containsSpamWord = spamKeywords.some(
      word =>
        formData.message.toLowerCase().includes(word) ||
        formData.subject.toLowerCase().includes(word),
    )

    if (containsSpamWord) {
      console.log('Mots-clés spam détectés')
      return
    }

    const mailtoUrl = `mailto:${recipientEmail}?subject=${encodeURIComponent(
      formData.subject,
    )}&body=${encodeURIComponent(formData.message)}`

    window.location.href = mailtoUrl
    onClose()
    setFormData({ subject: '', message: '' })
    setCaptchaValue('')
    setCaptchaError('')
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className='bg-white rounded-lg'>
        <div className='bg-anthracite text-white p-4 rounded-t-lg flex items-center justify-center'>
          <IoMdMail className='text-2xl mr-2' />
          <h2 className='text-xl font-semibold'>
            {t('contact')} {recipientName}
          </h2>
        </div>

        <div className='p-6'>
          <div className='space-y-4'>
            {/* Honeypot - caché visuellement mais accessible aux bots */}
            <div className='opacity-0 absolute -left-[9999px] -top-[9999px] h-0 w-0 overflow-hidden'>
              <input
                ref={honeyPotRef}
                type='text'
                name='website'
                tabIndex={-1}
                autoComplete='off'
              />
            </div>

            <MyInput
              label={t('subject')}
              value={formData.subject}
              onChange={value => handleInputChange('subject', value)}
              required
            />

            <div className='relative'>
              <textarea
                value={formData.message}
                onChange={e => handleInputChange('message', e.target.value)}
                placeholder={t('your-message')}
                className='w-full h-32 p-3 border rounded-lg bg-grisclair focus:outline-none  resize-none'
                required
              />
            </div>

            {/* Captcha */}
            <div className='bg-grisclair p-4 rounded-lg'>
              <p className='text-sm mb-4'>
                {t('solve-captcha')}: {captcha.num1} + {captcha.num2} = ?
              </p>
              <MyInput
                label={t('captcha')}
                value={captchaValue}
                onChange={setCaptchaValue}
                error={captchaError}
                type='number'
                min={0}
                required
              />
            </div>

            <div className='flex justify-end gap-2 mt-4'>
              <MyButton
                variant='anthracite-redpink'
                onClick={onClose}
                type='button'
              >
                {t('cancel')}
              </MyButton>
              <MyButton
                variant='redpink-anthracite'
                onClick={handleSubmit}
                type='button'
                disabled={
                  !formData.subject || !formData.message || !captchaValue
                }
              >
                {t('send')}
              </MyButton>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default SendMailModal
